import type { PropsWithChildren } from "react";

import type { QueryStatus } from "@tanstack/react-query";

import type { LeadsSearchRow } from "app/pages/sales-intelligence/modules/leads-search";
import type { SignalEventType } from "app/pages/sales-intelligence/sub-modules/account-overview/company/services/signals/types";
import SignalsEventsTooltipContent from "app/pages/sales-intelligence/sub-modules/signals/components/tooltips/SignalsEventsTooltipContent/SignalsEventsTooltipContent";
import SignalsTooltip from "app/pages/sales-intelligence/sub-modules/signals/components/tooltips/SignalsTooltip/SignalsTooltip";
import type SignalEventsTooltipViewModel from "app/pages/sales-intelligence/sub-modules/signals/models/view/signals-tooltip/SignalEventsTooltipViewModel";
import { createTooltipId } from "app/pages/sales-intelligence/sub-modules/signals/utils/tableColumns";

interface ISignalsEventsTooltipProps<V extends LeadsSearchRow> {
    row: V;
    isEnabled: boolean;
    fetchStatus: QueryStatus;
    redirectLink: URL["href"];
    createViewModel(eventsLimit: number): Readonly<SignalEventsTooltipViewModel>;
    getSignalTypeLink(eventType: SignalEventType): URL["href"];
    onViewAll(): void;
    onSignalTypeClick(signalType: SignalEventType): void;
    onSignalArticleClick(signalType: SignalEventType, articleTitle: string): void;
}

export const SignalsEventsTooltipContainer = <V extends LeadsSearchRow>({
    isEnabled,
    children,
    fetchStatus,
    redirectLink,
    getSignalTypeLink,
    onViewAll,
    onSignalTypeClick,
    onSignalArticleClick,
    row,
    createViewModel,
}: PropsWithChildren<ISignalsEventsTooltipProps<V>>) => {
    const tooltipKey = createTooltipId(row);

    const getStatefulTooltipKey = (status: QueryStatus): string => {
        // Unmounting tooltip content on fetch state change to fix incorrect tooltip positioning on content change
        if (isEnabled) {
            return `${tooltipKey}-${status}`;
        }

        return tooltipKey;
    };

    return (
        <SignalsTooltip
            key={getStatefulTooltipKey(fetchStatus)}
            enabled={isEnabled}
            content={
                <SignalsEventsTooltipContent
                    state={fetchStatus}
                    redirectLink={redirectLink}
                    createViewModel={createViewModel}
                    getSignalTypeLink={getSignalTypeLink}
                    onViewAll={onViewAll}
                    onSignalTypeClick={onSignalTypeClick}
                    onSignalArticleClick={onSignalArticleClick}
                />
            }
        >
            <>{children}</>
        </SignalsTooltip>
    );
};
